// src/features/analytics/components/BroadcastPicker.tsx
//
// Broadcast picker (dashboard ADR-009, amended 2026-09-13): the stream's live
// presence runs over the lookback (floored to the product window), newest
// first, with a run still on the air marked "In progress". Picking one writes
// the event window to the URL — the run itself, padded by an hour each side
// when "Include the hour before and after" is on (the named customer ask).
// Four states, one source of truth (broadcastPickerState): loading, error
// (with Retry), empty (copy names the real floor), ready.

import { useState } from 'react';
import { Button, Group, Select, Switch } from '@mantine/core';
import { BROADCAST_PAD_SECONDS } from '@/lib/constants/analytics';
import type { DayZone } from '@/shared/utils/utcRange';
import {
  broadcastMatchingWindow,
  broadcastOptionLabel,
  broadcastPickerPlaceholder,
  broadcastPickerState,
  broadcastValue,
  findBroadcast,
} from '../broadcasts';
import { broadcastWindow, type EventWindow } from '../eventWindow';
import type { BroadcastRow } from '../types';

interface BroadcastPickerProps {
  rows: BroadcastRow[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Copy for the empty list — names the product-window floor when it bounds the list. */
  emptyCopy: string;
  /** The explicit window in the URL, if any — used to show the matching broadcast as selected. */
  value: EventWindow | null;
  onPick: (window: EventWindow | null) => void;
  zone: DayZone;
  nowSeconds: number;
}

export function BroadcastPicker({
  rows,
  isLoading,
  isError,
  onRetry,
  emptyCopy,
  value,
  onPick,
  zone,
  nowSeconds,
}: BroadcastPickerProps) {
  const [pad, setPad] = useState(true);
  const selected = value ? broadcastMatchingWindow(rows, value.from, value.to, BROADCAST_PAD_SECONDS) : null;
  const state = broadcastPickerState(isLoading, isError, rows.length);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap" w="100%">
      <Select
        label="Broadcast"
        placeholder={broadcastPickerPlaceholder(state, emptyCopy)}
        data={rows.map((row) => ({ value: broadcastValue(row), label: broadcastOptionLabel(row, zone, nowSeconds) }))}
        value={selected ? broadcastValue(selected) : null}
        onChange={(next) => {
          const row = findBroadcast(rows, next);
          onPick(row ? broadcastWindow(row, pad) : null);
        }}
        disabled={state !== 'ready'}
        error={state === 'error' ? 'The list did not load.' : undefined}
        searchable
        clearable
        size="sm"
        w={{ base: '100%', sm: 380 }}
        limit={50}
        nothingFoundMessage="No broadcast matches"
      />
      {state === 'error' && (
        <Button variant="light" size="sm" onClick={onRetry}>
          Retry
        </Button>
      )}
      <Switch
        size="sm"
        label="Include the hour before and after"
        checked={pad}
        disabled={state !== 'ready'}
        onChange={(event) => {
          const next = event.currentTarget.checked;
          setPad(next);
          if (selected) onPick(broadcastWindow(selected, next));
        }}
      />
    </Group>
  );
}
